// src/utils/mobileNotifications.ts
// Using BuildNatively's NativelyNotifications API (Official Documentation)

declare global {
  interface Window {
    natively?: {
      sendPushNotification?: any;
      setDebug?: (enabled: boolean) => void;
    };
    NativelyNotifications?: any;
  }
}

import { useAuthStore } from '../store/authStore';

// Main setup function using BuildNatively's official API
export function setupMobileNotifications(userId: string) {
  console.log('📱 [SETUP] Setting up OneSignal for user:', userId);
  
  // Debug what's actually available
  console.log('🔍 [DEBUG] window.natively:', (window as any).natively);
  console.log('🔍 [DEBUG] window.NativelyNotifications:', (window as any).NativelyNotifications);
  console.log('🔍 [DEBUG] NativelyNotifications type:', typeof (window as any).NativelyNotifications);
  
  try {
    // Check if NativelyNotifications is available (directly on window, not inside natively)
    if (typeof window !== 'undefined' && (window as any).NativelyNotifications) {
      
      console.log('📱 [SETUP] ✅ NativelyNotifications found');
      
      // It IS a constructor - use new
      const notifications = new (window as any).NativelyNotifications();
      
      // Add a small delay to ensure SDK is fully initialized
      setTimeout(() => {
        // Step 1: Request permission first (required for both iOS & Android)
        const push_register_callback = function (resp: any) {
          alert(`📱 Permission response: ${JSON.stringify(resp)}`);
          console.log('📱 [PERMISSION] Permission response:', resp);
          
          if (resp && resp.status === true) {
            alert(`✅ Permission granted! Getting Player ID...`);
            console.log('📱 [PERMISSION] ✅ Permission granted');
            
            // Add delay before getting player ID to ensure OneSignal is ready
            setTimeout(() => {
              // Step 2: Get OneSignal Player ID after permission granted
              const onesignal_playerid_callback = function (playerResp: any) {
                alert(`🎯 Player ID response: ${JSON.stringify(playerResp)}`);
                console.log('📱 [PLAYER_ID] Player ID response:', playerResp);
                
                if (playerResp && playerResp.playerId && playerResp.playerId.trim() !== '') {
                  alert(`✅ Player ID found: ${playerResp.playerId.slice(0, 8)}...`);
                  console.log('📱 [PLAYER_ID] ✅ Player ID found:', playerResp.playerId);
                  storePlayerIdInProfile(userId, playerResp.playerId);
                } else {
                  alert(`❌ No Player ID received`);
                  console.log('📱 [PLAYER_ID] ❌ No Player ID received');
                }
              };
              
              // Get Player ID after permission granted
              notifications.getOneSignalId(onesignal_playerid_callback);
            }, 1000); // Give OneSignal time to register after permission
            
          } else {
            alert(`❌ Permission denied: ${JSON.stringify(resp)}`);
            console.log('📱 [PERMISSION] ❌ Permission denied');
          }
        };
        
        // FIXED: Callback should be first parameter
        const fallbacktosettings = false; // Don't show alert if permission denied
        notifications.requestPermission(push_register_callback, fallbacktosettings);
      }, 500); // Small delay to ensure SDK is ready
      
      return true;
    } else {
      alert(`❌ Natively SDK or NativelyNotifications not found
natively: ${typeof (window as any).natively}
NativelyNotifications: ${typeof (window as any).NativelyNotifications}`);
      console.log('📱 [SETUP] ❌ NativelyNotifications not available');
      return false;
    }
  } catch (error) {
    alert(`💥 Setup error: ${error.message}`);
    console.error('📱 [SETUP] ❌ Setup failed:', error);
    return false;
  }
}

// Store Player ID in user profile
async function storePlayerIdInProfile(userId: string, playerId: string) {
  try {
    alert(`💾 Storing Player ID in database...`);
    console.log('📱 [STORE] Storing Player ID:', playerId.slice(0, 8) + '...');
    
    await useAuthStore.getState().updateProfile({
      onesignal_player_id: playerId
    });
    
    alert(`✅ Player ID stored successfully!`);
    console.log('📱 [STORE] ✅ Player ID stored successfully');
  } catch (error) {
    alert(`💥 Database error: ${error.message}`);
    console.error('📱 [STORE] ❌ Failed to store Player ID:', error);
  }
}

// Wait for Natively SDK to be ready
export function waitForOneSignal(userId: string, maxAttempts = 30) {
  let attempts = 0;
  
  const checkNativelyNotifications = () => {
    attempts++;
    console.log(`📱 [WAIT] Checking NativelyNotifications (attempt ${attempts}/${maxAttempts})`);
    
    if (typeof window !== 'undefined' && (window as any).NativelyNotifications) {
      console.log('📱 [WAIT] ✅ NativelyNotifications found after waiting!');
      setupMobileNotifications(userId);
      return;
    }
    
    if (attempts < maxAttempts) {
      setTimeout(checkNativelyNotifications, 1000);
    } else {
      console.log('📱 [WAIT] ❌ NativelyNotifications timeout');
    }
  };
  
  checkNativelyNotifications();
}

// Immediate setup - simplified version
export async function setupNotificationsImmediately(userId: string): Promise<boolean> {
  console.log('📱 [IMMEDIATE] Attempting immediate notification setup...');
  alert('📱 Starting immediate notification setup...');
  
  // Try immediate setup
  if (typeof window !== 'undefined' && (window as any).NativelyNotifications) {
    console.log('📱 [IMMEDIATE] ✅ NativelyNotifications available immediately');
    alert('📱 ✅ NativelyNotifications ready! Setting up...');
    return setupMobileNotifications(userId);
  }
  
  // If not available, wait for it
  console.log('📱 [IMMEDIATE] NativelyNotifications not ready, waiting...');
  alert('📱 NativelyNotifications not ready, waiting...');
  
  return new Promise((resolve) => {
    waitForOneSignal(userId);
    
    // Set a timeout to resolve the promise
    setTimeout(() => {
      resolve(false);
    }, 30000);
  });
}

// Get current notification status
export function getNotificationStatus(): Promise<{
  playerId: string | null;
  isSubscribed: boolean;
  hasPermission: boolean;
}> {
  return new Promise((resolve) => {
    console.log('📊 [STATUS] Getting notification status...');
    
    if (typeof window !== 'undefined' && (window as any).NativelyNotifications) {
      try {
        // FIXED: Call as function, not constructor
        const notifications = (window as any).NativelyNotifications();
        let playerId: string | null = null;
        let hasPermission = false;
        let checksCompleted = 0;
        
        // Check permission status
        const push_permission_callback = (resp: any) => {
          console.log('📊 [STATUS] Permission status:', resp);
          hasPermission = resp?.status === true;
          checksCompleted++;
          
          if (checksCompleted === 2) {
            resolve({
              playerId,
              isSubscribed: !!(playerId && playerId.trim() !== ''),
              hasPermission
            });
          }
        };
        
        // Check Player ID
        const player_id_callback = (resp: any) => {
          console.log('📊 [STATUS] Player ID status:', resp);
          playerId = resp?.playerId || null;
          checksCompleted++;
          
          if (checksCompleted === 2) {
            resolve({
              playerId,
              isSubscribed: !!(playerId && playerId.trim() !== ''),
              hasPermission
            });
          }
        };
        
        // Get both permission status and player ID
        notifications.getPermissionStatus(push_permission_callback);
        notifications.getOneSignalId(player_id_callback);
        
      } catch (error) {
        console.error('📊 [STATUS] Error getting status:', error);
        resolve({ playerId: null, isSubscribed: false, hasPermission: false });
      }
    } else {
      console.log('📊 [STATUS] NativelyNotifications not available');
      resolve({ playerId: null, isSubscribed: false, hasPermission: false });
    }
  });
}

// Send push notification using BuildNatively's API (for testing)
export async function sendTestNotification(
  oneSignalAppId: string, 
  playerIds: string[], 
  title: string, 
  description: string,
  redirectUrl?: string
) {
  try {
    if (typeof window !== 'undefined' && (window as any).natively?.sendPushNotification) {
      const payload = {
        title,
        description,
        redirect_url: redirectUrl || window.location.origin
      };
      
      const isPreview = false; // Set to true if testing in Natively Preview app
      
      const resp = await (window as any).natively.sendPushNotification(
        oneSignalAppId, 
        payload, 
        playerIds, 
        isPreview
      );
      
      console.log('📤 [SEND] Push notification sent:', resp);
      return resp;
    } else {
      throw new Error('Natively sendPushNotification not available');
    }
  } catch (error) {
    console.error('📤 [SEND] Failed to send push notification:', error);
    throw error;
  }
}

export async function waitForPlayerId(retries = 5, delay = 1000){
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const tryGetPlayerId = () => {
      let notifications;
      if (typeof window.NativelyNotifications === "function") {
        notifications = new window.NativelyNotifications();
      } else if (typeof window.NativelyNotifications === "object") {
        notifications = window.NativelyNotifications;
      } else {
        console.error("❌ NativelyNotifications not available — unknown type");
        reject(new Error("NativelyNotifications not available"));
        return;
      }

      notifications.getOneSignalId((resp) => {
        console.log(`📱 [RETRY PLAYER_ID] Attempt ${attempts + 1}:`, resp);

        if (resp?.playerId && resp.playerId.trim() !== "") {
          console.log("✅ [RETRY PLAYER_ID] Success:", resp.playerId);
          resolve(resp.playerId);
        } else if (attempts < retries) {
          attempts++;
          setTimeout(tryGetPlayerId, delay);
        } else {
          console.warn("⚠️ [RETRY PLAYER_ID] Failed after retries");
          reject(new Error("Failed to get Player ID after retries"));
        }
      });
    };

    tryGetPlayerId();
  });
};
